import React, { useRef, useEffect, useContext, useState } from 'react'
import { graphql } from 'gatsby'
import SEO from '../components/SEO'
import Helmet from 'react-helmet'
import CollectionDetails from '../components/collection/CollectionDetails'
import Guarantee from '../components/collection/Guarantee'
import CollectionSwitcher from '../components/collection/CollectionSwitcher'
import CollectionProducts from '../components/collection/CollectionProducts'
import CollectionBanner from '../components/collection/CollectionBanner'
import CollectionSwitcherAlt from '../components/collection/CollectionSwitcherAlt'
import AddDiscountToCart from '../components/shared/AddDiscountToCart'
import {
  useProductListViewedTracking,
  useCollectionPageViewedTracking,
} from '../hooks/tracking'
import { LayoutContext, useUI } from '../contexts'
import { useIsMobile } from '../hooks/useIsMobile'
import { useSchemaForLD } from '../hooks/useSchemaForLD'
import { useGlobal } from '../hooks/useGlobal'

const Collection = ({
  location,
  data: {
    contentfulCollection: {
      seoTitle,
      seoDescription,
      title,
      name,
      detail,
      slug,
      information,
      image,
      video,
      products,
      partnerText,
      partnerLogoLink,
      partnerLogo,
      collectionMenu,
      verticals,
      alternativeLayout,
      alternativeLayoutBannerDesktop,
      alternativeLayoutBannerMobile,
      alternativeLayoutBannerCopy,
    },
    allTrustPilotSummary: {
      nodes: [
        {
          score: { trustScore, stars: trustpilotStars },
          total: totalNumberOfReviews,
        },
      ],
    },
  },
}) => {
  const [hasMounted, setHasMounted] = useState(false)

  useEffect(() => {
    setHasMounted(true)
  }, [])

  if (!products) {
    products = []
  }

  const detailsEl = useRef()
  const isMobile = useIsMobile()
  const { node_locale, currency } = useContext(LayoutContext)

  const {
    product: {
      guaranteeLink,
      guaranteeImage,
      guaranteeCopy,
      guaranteePeriod,
      guaranteePoints,
    },
    blackFriday: { heroText12022, heroText22022, heroColor1, heroColor2 },
  } = useGlobal(['product', 'blackFriday'], node_locale)

  const hasSubCollection = collectionMenu ? true : false
  const viewDetails = () => {
    window.scrollTo({
      top: isMobile
        ? detailsEl.current.offsetTop - 50
        : detailsEl.current.offsetTop - 100,
      behavior: 'smooth',
    })
  }

  useEffect(() => {
    if (hasMounted && window.dataLayer) {
      window.dataLayer.push({
        event: 'CollectionPageView',
        ecommerce: {
          currencyCode: 'EUR',
          detail: {
            collection: [
              {
                slug: location.pathname,
                name: title,
                brand: 'Stox',
                category: 'Socks',
              },
            ],
          },
        },
      })
    }

    // give currency a moment to load
    setTimeout(() => {
      // useProductListViewedTracking({
      //   currency: currency?.code,

      //   locale: node_locale,
      //   name: title,
      //   products,
      //   verticals,
      // })
      // for FB tracking
      useCollectionPageViewedTracking({
        currency: currency?.code,

        locale: node_locale,
        name: title,
        products,
        verticals,
      })
    }, 1500)
  }, [hasMounted])

  if (!hasMounted) return null

  return (
    <>
      <AddDiscountToCart location={location} />
      <SEO
        isPage
        location={location}
        title={seoTitle}
        description={seoDescription}
        image={image ? image.file.url : null}
        pageCategory={'Collection'}
        referrer={location.state?.referrer}
        schemas={[
          useSchemaForLD('breadcrumb', {
            item: location.pathname,
            name: title,
            position: 2,
          }),
          useSchemaForLD('faq', {
            information: information,
          }),
        ]}
      />

      {collectionMenu && isMobile && !alternativeLayout && (
        <CollectionSwitcher collections={collectionMenu} location={location} />
      )}
      {alternativeLayout && (
        <>
          <CollectionBanner
            bannerImageDesktop={alternativeLayoutBannerDesktop}
            bannerImageMobile={alternativeLayoutBannerMobile}
            bannerCopy={alternativeLayoutBannerCopy}
            heroCopy1={heroText12022}
            heroCopy2={heroText22022}
            heroCopyColor1={heroColor1}
            heroCopyColor2={heroColor2}
            alternativeLayout={alternativeLayoutBannerCopy}
          />
          <CollectionSwitcherAlt
            collections={collectionMenu}
            currentCollection={slug}
            location={location}
          />
        </>
      )}
      <CollectionProducts
        name={name}
        products={products}
        title={title}
        detail={detail}
        search={location.search}
        hasSubCollection={hasSubCollection}
        collectionProducts={products}
        onViewDetails={() => viewDetails()}
        partnerText={partnerText}
        partnerLogoLink={partnerLogoLink}
        partnerLogo={partnerLogo}
        image={image}
        information={information}
        trustpilotStars={trustpilotStars}
        totalNumberOfReviews={totalNumberOfReviews}
        video={video}
        alternativeLayout={alternativeLayout}
        position={1}
        location={location}
      />
      {information && (
        <CollectionDetails
          ref={detailsEl}
          information={information}
          title={title}
          position={2}
        />
      )}
      <Guarantee
        guaranteeImage={guaranteeImage}
        guaranteeCopy={guaranteeCopy}
        guaranteePeriod={guaranteePeriod}
        guaranteeLink={guaranteeLink}
        guaranteePoints={guaranteePoints}
        viewType={'Collection Page'}
        position={3}
      />
    </>
  )
}

export const ProductQuery = graphql`
  query Collection($id: String!) {
    contentfulCollection(id: { eq: $id }) {
      seoTitle
      seoDescription
      title
      name
      slug
      detail {
        raw
      }
      information {
        title
        content {
          references {
            ... on ContentfulAsset {
              __typename
              contentful_id
              file {
                url
              }
              description
            }
            ... on ContentfulAccordionLink {
              contentful_id
              slug
            }
          }
          raw
        }
      }
      products {
        availableInCountry
        title
        family {
          name
          legacySkus
        }
        gender {
          name
          title
        }
        label
        logo {
          title
          fluid(sizes: "(max-width: 1023px) 92vw, 41vw") {
            ...GatsbyContentfulFluid_withWebp_noBase64
          }
        }
        slug
        thumbnailSubheading
        thumbnailImage {
          title
          fluid(sizes: "(max-width: 1023px) 50vw, 30vw") {
            ...GatsbyContentfulFluid_withWebp_noBase64
          }
          file {
            url
          }
        }
        shopifyData {
          shopifyId
        }
        shopifyDataUs {
          shopifyId
        }
        shopifyProduct {
          id
          handle
          variants {
            edges {
              node {
                id
                price
              }
            }
          }
        }
      }
      partnerText
      partnerLogoLink
      partnerLogo {
        title
        fluid(sizes: "(max-width: 1023px) 20vw, 6vw") {
          ...GatsbyContentfulFluid_withWebp_noBase64
        }
      }
      image {
        title
        file {
          url
        }
        fluid(sizes: "(max-width: 1023px) 100vw, 30vw") {
          ...GatsbyContentfulFluid_withWebp_noBase64
        }
      }
      video {
        videoMp4 {
          file {
            url
          }
        }
        videoWebm {
          file {
            url
          }
        }
        poster {
          fluid(sizes: "(max-width: 1023px) 92vw, 41vw") {
            ...GatsbyContentfulFluid_withWebp_noBase64
          }
          title
        }
      }
      collectionMenu {
        title
        links {
          availableInCountry
          title
          target {
            __typename
            ...linkTarget
          }
        }
      }
      verticals
      alternativeLayout
      alternativeLayoutBannerDesktop {
        title
        fluid(sizes: "(max-width: 100vw) 100vw, 100vw") {
          ...GatsbyContentfulFluid_withWebp_noBase64
        }
      }
      alternativeLayoutBannerMobile {
        title
        fluid(sizes: "(max-width: 100vw) 100vw, 100vw") {
          ...GatsbyContentfulFluid_withWebp_noBase64
        }
      }
      alternativeLayoutBannerCopy
    }
    allTrustPilotSummary {
      nodes {
        score {
          trustScore
          stars
        }
        total
      }
    }
  }
`

export default Collection
